import { Plugin, PluginKey } from 'prosemirror-state'
import type { EditorState } from 'prosemirror-state'
import type { EditorView } from 'prosemirror-view'

import { findOffset } from '#util/scrolling'

import { getPaginationBreakdown } from './inlinePageBreaks'
import { getPagination } from './pagination'
import type { PluginFactory } from './types'

// https://prosemirror.net/docs/ref/#view.EditorProps.scrollThreshold
const SCROLL_MARGIN = { top: 100, right: 0, bottom: 32, left: 0 }

type ScrollTarget = 'default' | 'page'

const scrollManagerKey = new PluginKey<ScrollTarget>('scroll-manager')

declare module 'prosemirror-state' {
  interface Transaction {
    setMeta(key: 'scroll-target', value: ScrollTarget): this
    getMeta(key: 'scroll-target'): ScrollTarget | undefined
  }
}

export const scrollManagerPlugin: PluginFactory = () =>
  new Plugin<ScrollTarget>({
    key: scrollManagerKey,
    state: {
      init: () => 'default',
      apply(tr, prev) {
        const meta = tr.getMeta('scroll-target')
        if (meta) return meta
        if (tr.scrolledIntoView) return 'default'
        return prev
      },
    },
    props: {
      scrollThreshold: SCROLL_MARGIN,
      scrollMargin: SCROLL_MARGIN,
      handleScrollToSelection(view) {
        const mode = scrollManagerKey.getState(view.state)
        if (mode === 'page') return scrollSelectionToPageTop(view)
        return false // Let the default scrollTo handler run
      },
    },
  })

/**
 * Attempts to orient the user to the top of the page
 */
function scrollSelectionToPageTop(view: EditorView): boolean {
  const { head } = view.state.selection

  const pageNumber = getPageNumberAt(view.state, head)
  if (!pageNumber) return false

  const pageEl = document.getElementById(`page-${pageNumber}`)
  if (!pageEl) return false

  const scrollContainer = getScrollContainer(view)
  if (!scrollContainer) return false

  scrollContainer.scrollTo({
    top: findOffset(scrollContainer, pageEl) + 2,
    behavior: 'smooth',
  })

  return true
}

function getPageNumberAt(state: EditorState, pos: number): number | null {
  // We still have two paginator implementations. Prefer the new paginator when
  // it is available; otherwise fall back to the inline pagination breakdown.
  const page = getPagination(state)?.pageAt(pos)
  if (page) return page.index + 1

  const breakdown = getPaginationBreakdown(state)
  const pageIndex = breakdown?.pageIndexAtPosition(pos) ?? -1
  if (pageIndex < 0) return null

  return breakdown?.decorationData[pageIndex]?.pageNumber ?? null
}

// This is a hard-coded assumption about how the React component rendering the
// editor defines the scrollable region of the doc
function getScrollContainer(view: EditorView) {
  return view.dom.closest<HTMLElement>('[data-editor-scroller]')
}
